import React, { useMemo, useState, useCallback, forwardRef, useImperativeHandle } from 'react';
import {
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  SortDescriptor,
} from '@nextui-org/react';
import { Settlement } from 'common/src/utils/types';
import { formatMoneyUSD, formatNumber } from '../../utils/functions';
import { useTranslation } from '../../../../common/src/hooks/useTranslation';
import { UNIFIED_TABLE_CLASSNAMES, TableExportRef } from '../../utils/table-styles';
import { exportTableToExcel } from '../../utils/export-to-excel';

export interface GroupAggregatedRow {
  key: string | null;
  total_fob: number;
  total_kilos: number;
  total_extra_revenue_usd: number;
}

interface Props {
  settlements?: Settlement[];
  aggregated?: GroupAggregatedRow[];
  kgOrBox: 'kg' | 'box';
  boxSize?: number;
  groupBy?: string;
  showWithRevenues?: boolean;
}

interface GroupRow {
  key: string;
  totalFob: number;
  totalKilos: number;
}

const GROUP_LABELS: Record<string, string> = {
  variety: 'VARIEDAD',
  caliber: 'CALIBRE',
  format: 'FORMATO',
  color: 'COLOR',
  market: 'MERCADO',
  label: 'ETIQUETA',
  fleet: 'TIPO FLETE',
  country: 'PAÍS',
  business_type: 'TIPO DE NEGOCIO',
};

const SettlementsVarietyFOBTable = forwardRef<TableExportRef, Props>(({
  settlements,
  aggregated,
  kgOrBox,
  boxSize = 5,
  groupBy = 'variety',
  showWithRevenues = false,
}, ref) => {
  const { t } = useTranslation('settlements');
  const [sortDescriptor, setSortDescriptor] = useState<SortDescriptor>({
    column: 'totalFob',
    direction: 'descending',
  });

  const groupedData = useMemo(() => {
    // Prefer server-aggregated data when provided. Server returns null keys
    // for empty dimensions — localize them to "Sin X" at render time.
    if (aggregated) {
      const fallbackLabel = (() => {
        switch (groupBy) {
          case 'market': return t('Sin Mercado');
          case 'country': return t('Sin País');
          case 'label': return t('Sin Etiqueta');
          case 'business_type': return t('Sin Tipo de Negocio');
          case 'fleet': return t('Sin Flete');
          default: return '';
        }
      })();
      return aggregated
        .filter((row) => row.key !== null || fallbackLabel)
        .map((row) => ({
          key: row.key ?? fallbackLabel,
          totalFob:
            row.total_fob +
            (showWithRevenues ? row.total_extra_revenue_usd : 0),
          totalKilos: row.total_kilos,
        }))
        .filter((row) => row.key);
    }

    const rawSettlements = settlements ?? [];
    const groups: Record<string, GroupRow> = {};

    const addToGroup = (key: string | null | undefined, fob: number, kilos: number) => {
      if (!key) return;
      if (!groups[key]) groups[key] = { key, totalFob: 0, totalKilos: 0 };
      groups[key].totalFob += fob;
      groups[key].totalKilos += kilos;
    };

    if (groupBy === 'market' || groupBy === 'fleet' || groupBy === 'country' || groupBy === 'business_type') {
      // Group at settlement level
      rawSettlements.forEach((s) => {
        let key: string;
        if (groupBy === 'market') {
          key = s.market?.name || t('Sin Mercado');
        } else if (groupBy === 'country') {
          key = s.market?.country || t('Sin País');
        } else if (groupBy === 'business_type') {
          key = s.business_type || t('Sin Tipo de Negocio');
        } else {
          const type = s.ship_trip?.ship?.type;
          key = type === 'A' ? t('Aéreo') : type === 'T' ? t('Terrestre') : t('Marítimo');
        }
        const kilos = s.settlement_details.reduce((sum, d) => sum + (d.kilos || 0), 0);
        const fob = s.settlement_details.reduce((sum, d) => sum + (d.usd_total_fob || 0), 0);
        addToGroup(key, fob, kilos);
      });
    } else {
      // Group at detail level
      rawSettlements.forEach((s) => {
        s.settlement_details?.forEach((d) => {
          let key: string | null = null;
          switch (groupBy) {
            case 'variety': key = d.variety; break;
            case 'caliber': key = d.caliber; break;
            case 'format': key = d.format; break;
            case 'color': key = d.color; break;
            case 'label': key = d.label || t('Sin Etiqueta'); break;
          }
          if (key) addToGroup(key, d.usd_total_fob, d.kilos);
        });
      });
    }

    return Object.values(groups);
  }, [settlements, aggregated, showWithRevenues, groupBy, t]);

  const grandTotalKilos = useMemo(
    () => groupedData.reduce((sum, v) => sum + v.totalKilos, 0),
    [groupedData]
  );
  const grandTotalFob = useMemo(
    () => groupedData.reduce((sum, v) => sum + v.totalFob, 0),
    [groupedData]
  );

  const multiplier = kgOrBox === 'kg' ? 1 : boxSize;
  const volumeLabel = kgOrBox === 'kg' ? 'KG' : `CEQ ${boxSize}KG`;
  const fobUnitLabel = kgOrBox === 'kg' ? 'FOB/KG' : `FOB/CEQ ${boxSize}KG`;
  const firstColLabel = t(GROUP_LABELS[groupBy] || 'VARIEDAD');

  const getVolume = (kilos: number) => (kgOrBox === 'kg' ? kilos : kilos / boxSize);
  const getFobPct = (fob: number) => (grandTotalFob > 0 ? (fob / grandTotalFob) * 100 : 0);
  const getVolPct = (kilos: number) => (grandTotalKilos > 0 ? (kilos / grandTotalKilos) * 100 : 0);
  const getFobPerUnit = (fob: number, kilos: number) =>
    kilos > 0 ? (fob / kilos) * multiplier : 0;

  const getSortValue = useCallback(
    (item: GroupRow, col: string): number => {
      switch (col) {
        case 'key': return 0;
        case 'totalFob': return item.totalFob;
        case 'fobPct': return getFobPct(item.totalFob);
        case 'volume': return getVolume(item.totalKilos);
        case 'volPct': return getVolPct(item.totalKilos);
        case 'fobPerUnit': return getFobPerUnit(item.totalFob, item.totalKilos);
        default: return 0;
      }
    },
    [grandTotalFob, grandTotalKilos, multiplier, kgOrBox, boxSize]
  );

  const sortedData = useMemo(() => {
    const col = sortDescriptor.column as string;
    const dir = sortDescriptor.direction === 'ascending' ? 1 : -1;

    return [...groupedData].sort((a, b) => {
      if (col === 'key') return dir * a.key.localeCompare(b.key);
      return dir * (getSortValue(a, col) - getSortValue(b, col));
    });
  }, [groupedData, sortDescriptor, getSortValue]);

  // TOTAL row, always visible at the top regardless of sort.
  const dataWithTotal = useMemo(() => {
    if (sortedData.length === 0)
      return [] as (GroupRow & { isTotal?: boolean })[];
    const totalRow: GroupRow & { isTotal?: boolean } = {
      key: t('TOTAL'),
      totalFob: grandTotalFob,
      totalKilos: grandTotalKilos,
      isTotal: true,
    };
    return [totalRow, ...sortedData];
  }, [sortedData, grandTotalFob, grandTotalKilos, t]);

  const handleExport = useCallback(() => {
    exportTableToExcel({
      fileName: `fob-por-${groupBy}`,
      sheetName: firstColLabel,
      headers: [firstColLabel, t('FOB TOTAL'), t('% FOB'), volumeLabel, t('% VOL.'), fobUnitLabel],
      rows: sortedData.map((item) => [
        item.key,
        Math.round(item.totalFob),
        grandTotalFob > 0 ? parseFloat(getFobPct(item.totalFob).toFixed(1)) : 0,
        Math.round(getVolume(item.totalKilos)),
        grandTotalKilos > 0 ? parseFloat(getVolPct(item.totalKilos).toFixed(1)) : 0,
        item.totalKilos > 0 ? parseFloat(getFobPerUnit(item.totalFob, item.totalKilos).toFixed(2)) : 0,
      ]),
    });
  }, [sortedData, grandTotalFob, grandTotalKilos, volumeLabel, fobUnitLabel, firstColLabel, groupBy, t]);

  useImperativeHandle(ref, () => ({ exportToExcel: handleExport }), [handleExport]);

  return (
    <Table
      aria-label={firstColLabel}
      sortDescriptor={sortDescriptor}
      onSortChange={setSortDescriptor}
      isHeaderSticky
      classNames={{
        ...UNIFIED_TABLE_CLASSNAMES,
        base: 'max-h-[600px] overflow-auto',
      }}
    >
      <TableHeader>
        <TableColumn key='key' allowsSorting>{firstColLabel}</TableColumn>
        <TableColumn key='totalFob' allowsSorting align='end'>{t('FOB TOTAL')}</TableColumn>
        <TableColumn key='fobPct' allowsSorting align='end'>{t('% FOB')}</TableColumn>
        <TableColumn key='volume' allowsSorting align='end'>{volumeLabel}</TableColumn>
        <TableColumn key='volPct' allowsSorting align='end'>{t('% VOL.')}</TableColumn>
        <TableColumn key='fobPerUnit' allowsSorting align='end'>{fobUnitLabel}</TableColumn>
      </TableHeader>
      <TableBody items={dataWithTotal}>
        {(item: GroupRow & { isTotal?: boolean }) => (
          <TableRow
            key={item.isTotal ? '__total__' : item.key}
            className={
              item.isTotal
                ? 'bg-default-100 dark:bg-default-50 font-semibold border-b-2 border-default-200 dark:border-default-100'
                : ''
            }
          >
            <TableCell>
              <span className={item.isTotal ? 'font-bold' : 'font-medium'}>
                {item.key}
              </span>
            </TableCell>
            <TableCell>
              <span className={item.isTotal ? 'font-semibold' : ''}>
                {formatMoneyUSD(item.totalFob, { decimals: 0 })}
              </span>
            </TableCell>
            <TableCell>
              {item.isTotal ? (
                <span className='font-semibold flex justify-end'>—</span>
              ) : grandTotalFob > 0 ? (
                <div className='flex items-center justify-end gap-2'>
                  <div className='w-12 h-1.5 rounded-full bg-default-200 overflow-hidden'>
                    <div
                      className='h-full rounded-full bg-primary-400'
                      style={{ width: `${Math.min(getFobPct(item.totalFob), 100)}%` }}
                    />
                  </div>
                  <span className='text-default-500 min-w-[3rem] text-right'>
                    {getFobPct(item.totalFob).toFixed(1)}%
                  </span>
                </div>
              ) : '—'}
            </TableCell>
            <TableCell>
              <span className={item.isTotal ? 'font-semibold' : ''}>
                {formatNumber(getVolume(item.totalKilos), { decimals: 0 })}
              </span>
            </TableCell>
            <TableCell>
              {item.isTotal ? (
                <span className='font-semibold flex justify-end'>—</span>
              ) : grandTotalKilos > 0 ? (
                <div className='flex items-center justify-end gap-2'>
                  <div className='w-12 h-1.5 rounded-full bg-default-200 overflow-hidden'>
                    <div
                      className='h-full rounded-full bg-primary-400'
                      style={{ width: `${Math.min(getVolPct(item.totalKilos), 100)}%` }}
                    />
                  </div>
                  <span className='text-default-500 min-w-[3rem] text-right'>
                    {getVolPct(item.totalKilos).toFixed(1)}%
                  </span>
                </div>
              ) : '—'}
            </TableCell>
            <TableCell>
              <span className={item.isTotal ? 'font-bold' : 'font-semibold'}>
                {item.totalKilos > 0
                  ? formatMoneyUSD(
                      getFobPerUnit(item.totalFob, item.totalKilos),
                      { decimals: 2 }
                    )
                  : '—'}
              </span>
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
});

export default React.memo(SettlementsVarietyFOBTable);
